/* External */
import {
  memo, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

/* Ott-common */
import { formatDate, isTodayOrTomorrowDay } from '@dtvgo/date-utils';

/* Providers */
import { useGtmLocalContext } from '../../../../providers/GtmLocalProvider';

/* Components */
import FocusableButton from '../../../../components/FocusableButton';
import LiveProgressBar from '../../../../components/LiveProgressBar';
import DetailContainer from '../../../../components/DetailContainer';
import { useCurrentTime } from '../../../../providers/CurrentTimeProvider';
import LiveDetail from '../../../../components/LiveDetail';
import ScrollView from '../../../../components/ScrollView';
import Tabs from '../../../../components/Tabs/Tabs';
import MoreLikeThisVodEpg from '../../../../components/MoreLikeThisVodEpg';
import DetailsSelector from '../../../../components/DetailsSelector';

/*  Storages */
import {
  increasePdpHistory,
  decreasePdpHistory,
  selectPdpHistory,
  increaseBackButtonHistory,
  selectBackButtonHistory,
} from '../../../../storage/pdpHistory';

/* Hooks */
import useLiveContentTexts from '../../../../hooks/useLiveContentTexts';
import useQuery from '../../../../hooks/useQuery';
import useCurrentSchedule from '../../../../hooks/useCurrentSchedule';
import useRedirectToPlayer from '../../../../hooks/useRedirectToPlayer';
import useChannel from '../../../../hooks/useChannel';
import useFetchContent from '../../../../hooks/useFetchContent';

/* Utils */
import {
  DOT_SEPARATOR,
  TIME_FORMAT_FOR_SCHEDULES,
  RoutesOriginCarousels,
  STARTED_PAGE_ID,
} from '../../../../utils/constants';
import { getIconFromBlockStatus } from '../../../../utils/iconBlockUtils';
import { noOp } from '../../../../utils/functions';
import useLockIcon from '../../../../hooks/useLockIcon';
import parentalControlBlock from '../../../../utils/parentalControlUtils';
import { Direction } from '../../../../utils/spatialNavigation';
import {
  isSpideoMoreLikeThisActive,
  timesPressedBackButtonOnPdp,
} from '../../../../utils/env';
import { redirectTo } from '../../../../utils/history';
import { getLink, Routes } from '../../../../utils/router';
import { PLAY_SOURCE_PAGE } from '../../../../utils/trackSourceConviva';
import {
  getRating,
  useTitle,
  joinArrayWithSeparator,
  isPlayingNow,
  joinArrayWithSpace,
  isSports,
  isSelfClassified,
} from '../../../../utils/metaDataUtils';
import {
  formatContentData,
  formatTimeToStart,
  GTM_CATEGORIES,
  GTM_EVENT_NAMES,
  GTM_SUBCATEGORIES,
  playerType,
  transmissionContentType,
} from '../../../../utils/googleTags';
import { useOnBackPress } from '../../../../utils/keyDownEvents';

/* Style */
import './Live.scss';

const requiredProps = ['title', 'description', 'rating', 'images', 'ratingDescription', 'ratingWarning'];

function Live({ sourceProvider, realFocusKey = '', setFocus = noOp }) {
  const { timeZone } = useIntl();
  const history = useHistory();
  const WATCH_NOW_BUTTON_FOCUS_KEY = `${realFocusKey}-watch-now`;
  const BACK_HOME_BUTTON_KEY = `${realFocusKey}-back-home-button`;
  const { state: locationState } = useLocation();
  const { commonTaggingEvent, gtmState: { pdpSelectedTabTitle } } = useGtmLocalContext();
  const tagsMountRef = useRef(false);
  const { channelId } = useParams();
  const { startTime: startTimeParam } = useQuery();
  const channel = useChannel(channelId, sourceProvider);
  const { schedule } = useCurrentSchedule(
    channel,
    false,
    startTimeParam && new Date(startTimeParam),
  );
  const pageId = sessionStorage.getItem(STARTED_PAGE_ID);

  const { content } = useFetchContent({
    contentId: schedule?.content,
    sourceProvider,
    requiredProps,
  });

  const { startTime, endTime } = schedule || {};

  const { description, vrioAssetId: contentId } = content || {};
  const timeLiveContentTexts = useLiveContentTexts();
  const currentTime = useCurrentTime();

  const startDateFormatted = useMemo(() => {
    const day = isTodayOrTomorrowDay(startTime, timeLiveContentTexts);
    const startTimeFormatted = startTime
      && formatDate(startTime, TIME_FORMAT_FOR_SCHEDULES, timeZone);
    return `${day} ${startTimeFormatted}`;
  }, [startTime, timeLiveContentTexts, timeZone]);

  const showLockIcon = useLockIcon(content);
  const isContentBlockedByParentalControl = parentalControlBlock(content);

  const iconName = getIconFromBlockStatus(showLockIcon, isContentBlockedByParentalControl);

  const metadata = useMemo(() => {
    if (content) {
      return joinArrayWithSeparator([
        startDateFormatted,
        channel?.title || schedule?.channelName,
      ], DOT_SEPARATOR);
    }
    return '';
  }, [content, startDateFormatted, channel?.title, schedule?.channelName]);

  const title = useTitle(content, schedule);

  const subtitle = useMemo(() => joinArrayWithSpace([
    getRating(content),
    metadata,
  ]), [content, metadata]);

  const [isScrolled, setIsScrolled] = useState(false);

  const dispatch = useDispatch();
  const pdpHistory = useSelector(selectPdpHistory);
  const backButtonHistory = useSelector(selectBackButtonHistory);
  const shouldDisplayBackHomeButton = isSpideoMoreLikeThisActive && pdpHistory.length > 1;

  /* eslint-disable-next-line react-hooks/exhaustive-deps
  -- phdHistory causes an unwanted side effect */
  const shouldAddIdToPdpHistory = useMemo(() => !pdpHistory.includes(contentId), [contentId]);

  useEffect(() => {
    if (isSpideoMoreLikeThisActive && shouldAddIdToPdpHistory && contentId) {
      dispatch(increasePdpHistory(contentId));
    }
  }, [contentId, dispatch, shouldAddIdToPdpHistory]);

  useEffect(() => {
    setIsScrolled(false);

    setFocus(WATCH_NOW_BUTTON_FOCUS_KEY);
  }, [WATCH_NOW_BUTTON_FOCUS_KEY, setFocus, contentId]);

  const handleBackHome = useCallback(() => {
    redirectTo(getLink(Routes.Private.Home));
  }, []);

  const handleOnRedirectToPlayer = useRedirectToPlayer(channel, {
    page: PLAY_SOURCE_PAGE.PDP,
    section: title || '',
    state: locationState,
  });

  const isThereTitleOrSchedules = useMemo(
    () => !!(title && startDateFormatted),
    [startDateFormatted, title],
  );

  const handleArrowPressTabs = useCallback((direction) => {
    if (direction === Direction.UP) {
      setIsScrolled(false);
      setFocus(WATCH_NOW_BUTTON_FOCUS_KEY);
      return false;
    }
    return true;
  }, [WATCH_NOW_BUTTON_FOCUS_KEY, setFocus]);

  const handleWheel = useCallback((event) => {
    setIsScrolled(event.deltaY > 0);
  }, []);

  const pdpCommonTagEvent = useCallback(({ eventName, subcategory }) => {
    if (!locationState) return;

    const {
      collectionName,
      componentType,
      gtmPlaySource,
      providerName,
      sectionName,
      transmissionType,
    } = locationState;
    const channelContent = content;

    commonTaggingEvent({
      category: GTM_CATEGORIES.PDP_MOVIE_SERIE_LIVE_DATA,
      subCategory: subcategory || GTM_SUBCATEGORIES.PDP.MOVIES_LIVE,
      eventName,
      data: {
        content: {
          ...formatContentData({
            content: channelContent,
            rest: {
              progress: channelContent?.progress,
              playSource: gtmPlaySource,
              isLive: true,
              channelId,
              channelName: schedule?.channelName,
            },
          }),
        },
        componentType,
        transmissionType: transmissionContentType(transmissionType),
        sectionName,
        collectionName,
        providerName,
        isSports: isSports(channelContent),
        playerType: playerType(channelContent?.type, true, isSports(channelContent)),
        filterPdp: pdpSelectedTabTitle,
        timeToStart: isPlayingNow(schedule, currentTime) ? 0 : formatTimeToStart({ startTime }),
      },
    });
  }, [
    channelId,
    commonTaggingEvent,
    content,
    currentTime,
    locationState,
    pdpSelectedTabTitle,
    schedule,
    startTime,
  ]);

  const ratingDescription = isSelfClassified(content) ? '' : content?.ratingDescription;
  const ratingWarning = isSelfClassified(content) ? '' : content?.ratingWarning;

  useEffect(() => {
    if (!tagsMountRef.current && content) {
      pdpCommonTagEvent({ eventName: GTM_EVENT_NAMES.PAGE_VIEW });
      tagsMountRef.current = true;
    }
  }, [content, pdpCommonTagEvent]);

  const handleOnEnterPress = useCallback(() => {
    handleOnRedirectToPlayer();
    pdpCommonTagEvent({
      eventName: GTM_EVENT_NAMES.PDP.WATCH_CHANNEL,
      subcategory: GTM_SUBCATEGORIES.PDP.MOVIES_LIVE,
    });
  }, [handleOnRedirectToPlayer, pdpCommonTagEvent]);

  const handleOnBack = useCallback(() => {
    if (backButtonHistory === timesPressedBackButtonOnPdp) {
      redirectTo(getLink(RoutesOriginCarousels[pageId]));
      return;
    }

    dispatch(increaseBackButtonHistory());
    dispatch(decreasePdpHistory(contentId));

    history.goBack();
  }, [backButtonHistory, dispatch, pageId, history, contentId]);

  useOnBackPress({
    listener: handleOnBack,
    when: isSpideoMoreLikeThisActive,
  });

  return (
    <div
      className={`dtv-html5-c-detail-live ${isScrolled ? 'dtv-html5-c-detail-live--scrolled' : ''}`}
      onWheel={handleWheel}
    >
      <DetailContainer
        content={content}
        isScrolled={isScrolled}
      >
        {shouldDisplayBackHomeButton && (
          <div className="dtv-html5-c-detail-live__content-back-home-button">
            <FocusableButton
              className="dtv-html5-c-detail-live__back-home-button"
              onEnterPress={handleBackHome}
              focusKey={BACK_HOME_BUTTON_KEY}
              icon="arrow-back"
              primary
            >
              <FormattedMessage id="pdp.backButtonTitle" />
            </FocusableButton>
          </div>
        )}
        <LiveDetail
          startDate={startTime}
          endDate={endTime}
          schedule={schedule}
          isPlayingNow={isPlayingNow(schedule, currentTime)}
        />
        {isThereTitleOrSchedules ? (
          <>
            <DetailContainer.Title className="dtv-html5-c-detail-live__detail-container__title">
              {title || ''}
            </DetailContainer.Title>

            <div className="dtv-html5-c-detail-live__detail-container__subtitle">
              {subtitle}
            </div>

            {description && (
              <DetailContainer.Description className="dtv-html5-c-detail-live__detail-container__description">
                {description || ''}
              </DetailContainer.Description>
            )}
          </>
        ) : (
          <span className="dtv-html5-c-detail-live__detail-container__missing-metadata">
            <FormattedMessage id="schedule.missing.metadata" />
          </span>
        )}
        <div className="dtv-html5-c-detail-live__detail-container__content-button">
          <FocusableButton
            focusKey={WATCH_NOW_BUTTON_FOCUS_KEY}
            icon={iconName}
            primary
            onEnterPress={handleOnEnterPress}
          >
            <FormattedMessage id={(showLockIcon || isContentBlockedByParentalControl) ? 'common.PlayButtonLockIcon' : 'live.seeChannel'} />
          </FocusableButton>
          <div className="dtv-html5-c-detail-live__detail-container__progress-bar">
            <LiveProgressBar startDate={startTime} endDate={endTime} className="dtv-html5-c-detail-live__detail-container__progress-bar" />
          </div>
        </div>
      </DetailContainer>
      <Tabs
        onScrolledChange={setIsScrolled}
        onArrowPress={handleArrowPressTabs}
        showActiveTab={isScrolled}
        loadDefaultTab
      >
        <div
          className="dtv-html5-c-detail-serie__content-episodes"
          data-labeltab="tabs.title.related.content"
        >
          <ScrollView
            resetScrollPosition={!isScrolled}
            propagateEventOnReachTop
          >
            {isSpideoMoreLikeThisActive && contentId
              && (
                <MoreLikeThisVodEpg
                  className="dtv-html5-c-detail-vod__more-like-this"
                  contentId={contentId}
                  pdpCommonTagEvent={pdpCommonTagEvent}
                  isDisplaying={isScrolled}
                />
              )}
          </ScrollView>
        </div>

        <div
          className="dtv-html5-c-detail-serie__content-episodes"
          data-labeltab="common.details"
        >
          <div className="dtv-html5-c-detail-serie__seasons">
            <DetailsSelector
              title={title}
              description={description}
              genres={content?.genres?.join(' • ')}
              ageRating={getRating(content)}
              ratingDescription={ratingDescription}
              ratingWarning={ratingWarning}
            />
          </div>
        </div>
      </Tabs>
    </div>
  );
}

Live.propTypes = {
  sourceProvider: PropTypes.string,
  realFocusKey: PropTypes.string,
  setFocus: PropTypes.func,
};

export default memo(withFocusable()(memo(Live)));
